import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { UserRole } from '~types/types';
import { apiRequest, HttpMethod } from '@api/common';
import authService from '../services/authService';

// API response type for current user
interface CurrentUserResponse {
    role: UserRole;
    terminals: string[];
}

// Context for user details (authentication is guaranteed by MsalAuthenticationTemplate)
interface AuthContextState {
    userName: string | null;
    role: UserRole;
    terminals: string[];
    loading: boolean;
    loginFailed: boolean;
    errorMessage: string;
}

const AuthContext = createContext<AuthContextState>({
    userName: null,
    role: UserRole.Unknown,
    terminals: [],
    loading: true,
    loginFailed: false,
    errorMessage: ''
});

export const useAuth = () => useContext(AuthContext);

// Provider that loads user details from MSAL account and API
// Authentication is already guaranteed by MsalAuthenticationTemplate in App.tsx
export const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { instance, accounts } = useMsal();
    const [userName, setUserName] = useState<string | null>(null);
    const [role, setRole] = useState<UserRole>(UserRole.Unknown);
    const [terminals, setTerminals] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loginFailed, setLoginFailed] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const loadUserDetails = async () => {
            try {
                // Account is guaranteed to exist here (inside MsalAuthenticationTemplate)
                const account = accounts[0];
                if (account) {
                    instance.setActiveAccount(account);
                    await authService.setActiveAccount(account);
                }

                // Get username from MSAL account
                const user = await authService.getUser();
                if (user) {
                    setUserName(user.userName);
                }

                // Fetch role and terminals from API
                const currentUser = await apiRequest<void, CurrentUserResponse>(
                    HttpMethod.GET,
                    'settings/currentuser'
                );

                setRole(currentUser.role);
                setTerminals(currentUser.terminals);
                setLoginFailed(false);
            } catch (error) {
                console.error('Error loading user details:', error);
                setLoginFailed(true);
                setErrorMessage(error instanceof Error ? error.message : 'Access denied');
            } finally {
                setLoading(false);
            }
        };

        loadUserDetails();
    }, [accounts, instance]);

    return (
        <AuthContext.Provider value={{ userName, role, terminals, loading, loginFailed, errorMessage }}>
            {children}
        </AuthContext.Provider>
    );
};

export default MsalAuthProvider;